import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminLogin, adminVerify2FA } from '../../api/auth';

export default function AdminLoginPage() {
  const navigate = useNavigate();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'credentials' | '2fa'>('credentials');
  const [code, setCode] = useState('');
  const codeRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      const result = await adminLogin({ username, password });
      if (result.requires2FA) {
        setStep('2fa');
        setTimeout(() => codeRef.current?.focus(), 50);
      } else {
        navigate('/admin');
      }
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Credenziali non valide.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handle2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      await adminVerify2FA(code.trim());
      navigate('/admin');
    } catch (err: any) {
      setError(err?.response?.data?.error ?? 'Codice non valido.');
      setCode('');
      codeRef.current?.focus();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)',
      padding: '1rem',
    }}>
      <div style={{
        background: 'var(--white)', borderRadius: 'var(--border-radius-lg)',
        boxShadow: 'var(--shadow-lg)', padding: '2.5rem', width: '100%', maxWidth: '400px',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{
            width: '64px', height: '64px', background: 'var(--primary)', borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1rem', fontSize: '1.75rem',
          }}>
            {step === '2fa' ? '🔐' : '🎓'}
          </div>
          <h1 style={{ fontSize: '1.5rem', color: 'var(--primary)', marginBottom: '0.25rem' }}>
            TUPLA LMS
          </h1>
          <p style={{ color: 'var(--gray-500)', margin: 0, fontSize: '0.9375rem' }}>
            {step === '2fa' ? 'Verifica in due passaggi' : 'Area Amministrativa'}
          </p>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ marginBottom: '1.25rem' }}>{error}</div>
        )}

        {step === 'credentials' && (
          <form onSubmit={handleCredentials}>
            <div className="form-group">
              <label className="form-label" htmlFor="username">Username</label>
              <input
                id="username" type="text" className="form-control"
                value={username} onChange={e => setUsername(e.target.value)}
                placeholder="admin" autoComplete="username" autoFocus required
              />
            </div>
            <div className="form-group">
              <label className="form-label" htmlFor="password">Password</label>
              <input
                id="password" type="password" className="form-control"
                value={password} onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" autoComplete="current-password" required
              />
            </div>
            <button type="submit" className="btn btn-primary w-full"
              style={{ marginTop: '0.5rem', padding: '0.75rem' }} disabled={isSubmitting}>
              {isSubmitting
                ? <><span className="spinner" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }} /> Accesso in corso...</>
                : 'Accedi'}
            </button>
          </form>
        )}

        {step === '2fa' && (
          <form onSubmit={handle2FA}>
            <p style={{ color: 'var(--gray-600)', fontSize: '0.9375rem', marginBottom: '1.5rem', lineHeight: 1.5 }}>
              Apri la tua app di autenticazione e inserisci il codice a 6 cifre.
            </p>
            <div className="form-group">
              <label className="form-label" htmlFor="totp-code">Codice di verifica</label>
              <input
                id="totp-code" ref={codeRef} type="text" inputMode="numeric"
                maxLength={6} className="form-control"
                value={code} onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="000000" autoComplete="one-time-code"
                style={{ letterSpacing: '0.3em', fontSize: '1.5rem', textAlign: 'center' }}
                required
              />
            </div>
            <button type="submit" className="btn btn-primary w-full"
              style={{ marginTop: '0.5rem', padding: '0.75rem' }}
              disabled={isSubmitting || code.length !== 6}>
              {isSubmitting
                ? <><span className="spinner" style={{ width: '1rem', height: '1rem', borderWidth: '2px' }} /> Verifica...</>
                : 'Verifica codice'}
            </button>
            <button type="button" className="btn btn-ghost w-full" style={{ marginTop: '0.5rem' }}
              onClick={() => { setStep('credentials'); setCode(''); setError(null); }}>
              ← Torna al login
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
